import { useEffect } from "react";
import Select from "react-select";
import { LuBarChart3, LuFileSpreadsheet } from "react-icons/lu";
import GameStatistics from "./GameStatistics";
import { useLazyGetGamesQuery, useLazyGetGameStatisticsQuery } from "../../store/services/gameServices";
import { useGetMunicipalitiesQuery } from "../../store/services/municipalitiesServices";
import { exportGameStatisticsToExcel } from "../../utils/exportGameStatisticsToExcel";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 12,
        borderColor: state.isFocused ? "var(--secondary-color)" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(210,171,103,0.18)" : "none",
        "&:hover": { borderColor: "var(--secondary-color)" },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? "var(--secondary-color)"
            : state.isFocused
            ? "rgba(210,171,103,0.1)"
            : "white",
        color: state.isSelected ? "white" : "#111827",
        cursor: "pointer",
    }),
};

export default function StatisticsPanel({ selectedGame, onSelectGame }) {
    const [getGames, { data: gamesData }] = useLazyGetGamesQuery();
    const [getGameStatistics, { data: gameStatistics, isFetching }] = useLazyGetGameStatisticsQuery();
    const { data: municipalities } = useGetMunicipalitiesQuery();

    useEffect(() => {
        getGames({});
    }, [getGames]);

    const handleSelect = async (value) => {
        onSelectGame(value);
        if (value) await getGameStatistics({ game_id: value.value }).unwrap();
    };

    const handleExport = () => {
        exportGameStatisticsToExcel({
            data: gameStatistics?.data,
            municipalities,
            gameTitle: selectedGame?.label,
        });
    };

    const options = gamesData?.data?.data?.map((g) => ({ value: g.game_id, label: g.title })) || [];
    const hasStats = !!gameStatistics?.data;

    return (
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-5 sm:p-6 flex flex-col gap-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
                <div className="lg:w-1/2 w-full">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                        Loja
                    </label>
                    <Select
                        name="game_id"
                        options={options}
                        styles={selectStyles}
                        placeholder="Zgjidh lojën..."
                        onChange={handleSelect}
                        value={selectedGame}
                        isClearable
                    />
                </div>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={!hasStats}
                    className="self-start lg:self-end inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-secondary hover:bg-secondary/90 active:scale-[0.98] text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <LuFileSpreadsheet size={16} />
                    Eksporto statistikat
                </button>
            </div>

            <div>
                {!selectedGame ? (
                    <EmptyHero
                        title="Zgjidhni një lojë për të parë statistikat"
                        subtitle="Pas zgjedhjes së lojës, do të shfaqen të dhënat për fituesit, kuponat dhe shpërndarjen sipas komunave."
                    />
                ) : isFetching ? (
                    <EmptyHero title="Duke ngarkuar..." subtitle="Po marrim të dhënat e lojës së zgjedhur." />
                ) : hasStats ? (
                    <GameStatistics data={gameStatistics.data} />
                ) : (
                    <EmptyHero
                        title="Nuk ka të dhëna për këtë lojë"
                        subtitle="Provo një lojë tjetër ose kontrollo më vonë."
                    />
                )}
            </div>
        </div>
    );
}

function EmptyHero({ title, subtitle }) {
    return (
        <div className="py-16 px-6 flex flex-col items-center text-center">
            <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-2xl" />
                <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center shadow-sm">
                    <LuBarChart3 size={32} className="text-secondary" />
                </div>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500 leading-relaxed">{subtitle}</p>
        </div>
    );
}
